import React, { useState, useEffect, useCallback, useContext } from 'react';
import { getAuth, multiFactor, sendEmailVerification, updatePassword } from 'firebase/auth';
import { useHistory } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Link from '@material-ui/core/Link';
import CircularProgress from '@material-ui/core/CircularProgress';
import Alert from '@material-ui/lab/Alert';
import MFAEnrollment from './MFAEnrollment';
import { GlobalContext } from '../../Context/Global.context';
import API, { GetOrganizationSecuritySettingsPathForApi } from '../../Util/api';

const isEmailVerified = (user) => {
    if (!user) return false;
    const hasPasswordProvider = user.providerData.some(p => p.providerId === 'password');
    if (!hasPasswordProvider) return true;
    return user.emailVerified;
};

const supportedMFAProviders = ['password', 'google.com', 'apple.com', 'microsoft.com', 'facebook.com', 'github.com'];
const isMFASupported = (user) => {
    if (!user) return false;
    return user.providerData.some(p => supportedMFAProviders.includes(p.providerId));
};

// Module-level: survives component remounts
let activeCheckPromise = null;

function doMFACheck(activeOrgId) {
    if (activeCheckPromise) {
        return activeCheckPromise;
    }

    activeCheckPromise = (async () => {
        try {
            let auth;
            try {
                auth = getAuth();
            } catch (e) {
                return { mfaRequired: false, mfaEnrolled: false, emailVerified: true, mfaSupported: true };
            }

            const user = auth.currentUser;
            if (!user) {
                return { mfaRequired: false, mfaEnrolled: false, emailVerified: true, mfaSupported: true };
            }

            const userMfaSupported = isMFASupported(user);

            // Check email verification first
            let userEmailVerified = isEmailVerified(user);

            // Sync email verification from OAuth providers
            try {
                await API.post('/user/sync-email-verification', null, { timeout: 10000 });
                await user.reload();
                const freshUser = auth.currentUser;
                if (freshUser) {
                    userEmailVerified = isEmailVerified(freshUser);
                }
            } catch (err) {
                console.debug('Email verification sync failed (non-critical):', err);
            }

            const currentUser = auth.currentUser || user;
            const enrolledFactors = multiFactor(currentUser).enrolledFactors;
            if (enrolledFactors.length > 0) {
                return { mfaRequired: false, mfaEnrolled: true, emailVerified: true, mfaSupported: true };
            }

            // Only check the active organization
            let required = false;
            if (activeOrgId) {
                try {
                    const resp = await API.get(GetOrganizationSecuritySettingsPathForApi(activeOrgId), { timeout: 10000 });
                    if (resp.data && resp.data.MFARequired) {
                        required = true;
                    }
                } catch (err) {
                    // Assume not required on error
                }
            }

            return { mfaRequired: required, mfaEnrolled: false, emailVerified: userEmailVerified, mfaSupported: userMfaSupported };
        } finally {
            activeCheckPromise = null;
        }
    })();

    return activeCheckPromise;
}

const MFAEnrollmentGate = ({ children }) => {
    const context = useContext(GlobalContext);
    const history = useHistory();

    const userPrefsReady = !!(context
        && context.CompletedGET
        && context.CompletedGET.UserPreferences
        && context.UserPreferences);

    const activeOrgId = userPrefsReady
        ? context.UserPreferences.ActiveOrganizationID
        : null;

    const [checking, setChecking] = useState(true);
    const [mfaRequired, setMfaRequired] = useState(false);
    const [mfaEnrolled, setMfaEnrolled] = useState(false);
    const [emailVerified, setEmailVerified] = useState(null);
    const [mfaSupported, setMfaSupported] = useState(true);
    const [sendingVerification, setSendingVerification] = useState(false);
    const [verificationMessage, setVerificationMessage] = useState('');
    const [verificationError, setVerificationError] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [settingPassword, setSettingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    // A session_v2 cookie can outlive its server-side login session (e.g. signing
    // out in another browser on the same machine expires the shared LoginSession).
    // The boot calls then all return 401; without this the gate spins forever.
    const sessionRejected = !!(context
        && context.ApiError
        && context.ApiError.response
        && context.ApiError.response.status === 401);

    useEffect(() => {
        if (!sessionRejected) return;
        const clear = 'session_v2=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        document.cookie = clear;
        document.cookie = clear + '; domain=.nucleus.one';
        history.replace('/login');
    }, [sessionRejected, history]);

    useEffect(() => {
        if (!userPrefsReady) return;

        let mounted = true;

        doMFACheck(activeOrgId)
            .then(result => {
                if (mounted) {
                    setMfaRequired(result.mfaRequired);
                    setMfaEnrolled(result.mfaEnrolled);
                    setEmailVerified(result.emailVerified);
                    setMfaSupported(result.mfaSupported);
                    setChecking(false);
                }
            })
            .catch(() => {
                if (mounted) setChecking(false);
            });

        return () => { mounted = false; };
    }, [userPrefsReady, activeOrgId]);

    // Last-resort fallback, mirroring Root.js: never leave the user on the
    // spinner indefinitely if a gating request hangs.
    useEffect(() => {
        if (!checking) return;
        const timer = setTimeout(() => setChecking(false), 15000);
        return () => clearTimeout(timer);
    }, [checking]);

    const handleEnrollmentComplete = useCallback(() => {
        setMfaEnrolled(true);
        setMfaRequired(false);
    }, []);

    const handleSendVerificationEmail = async () => {
        setSendingVerification(true);
        setVerificationError('');
        setVerificationMessage('');
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            await sendEmailVerification(user);
            setVerificationMessage('Verification email sent! Please check your inbox and click the link.');
        } catch (err) {
            console.error('Error sending verification email:', err);
            setVerificationError(err.message || 'Failed to send verification email.');
        } finally {
            setSendingVerification(false);
        }
    };

    const handleCheckVerification = async () => {
        setVerificationError('');
        setVerificationMessage('');
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                const verified = isEmailVerified(user);
                setEmailVerified(verified);

                if (verified) {
                    setVerificationMessage('Email verified! You can now set up MFA.');
                    return;
                }

                try {
                    await API.post('/user/sync-email-verification');
                } catch (err) {
                    console.debug('Email verification sync failed:', err);
                }

                await user.reload();
                const freshUser = auth.currentUser;
                if (freshUser) {
                    const freshVerified = isEmailVerified(freshUser);
                    setEmailVerified(freshVerified);
                    if (freshVerified) {
                        setVerificationMessage('Email verified! You can now set up MFA.');
                    } else {
                        setVerificationError('Email not verified yet. Please check your inbox and click the verification link.');
                    }
                }
            }
        } catch (err) {
            console.error('Error checking verification:', err);
            setVerificationError('Failed to check verification status.');
        }
    };

    if (checking) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </div>
        );
    }

    const handleSetPassword = async () => {
        setPasswordError('');
        if (newPassword.length < 6) {
            setPasswordError('Password must be at least 6 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError('Passwords do not match.');
            return;
        }
        setSettingPassword(true);
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            await updatePassword(user, newPassword);
            // Reload to pick up the new password provider
            await user.reload();
            setMfaSupported(true);
            setEmailVerified(true);
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            console.error('Error setting password:', err);
            if (err.code === 'auth/requires-recent-login') {
                setPasswordError('Your session has expired. Please sign out and sign back in, then try again.');
            } else if (err.code === 'auth/weak-password') {
                setPasswordError('Password is too weak. Please choose a stronger password.');
            } else {
                setPasswordError(err.message || 'Failed to set password.');
            }
        } finally {
            setSettingPassword(false);
        }
    };

    if (mfaRequired && !mfaEnrolled) {
        // User's sign-in method doesn't support MFA (e.g. LinkedIn custom token)
        // Prompt them to set a password to add a supported first factor
        if (!mfaSupported) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
                    <Typography variant="h6" style={{ marginBottom: 16 }}>
                        Your organization requires multi-factor authentication
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24, maxWidth: 500, textAlign: 'center' }}>
                        To enable MFA, you need to set a password for your account. This will be used as a backup sign-in method.
                    </Typography>

                    {passwordError && (
                        <Alert severity="error" style={{ marginBottom: 16, maxWidth: 400 }} onClose={() => setPasswordError('')}>
                            {passwordError}
                        </Alert>
                    )}

                    <div style={{ width: '100%', maxWidth: 400 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            type="password"
                            label="New password"
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            style={{ marginBottom: 16 }}
                        />
                        <TextField
                            fullWidth
                            variant="outlined"
                            type="password"
                            label="Confirm password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSetPassword(); }}
                            style={{ marginBottom: 16 }}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={handleSetPassword}
                            disabled={settingPassword}
                        >
                            {settingPassword ? <CircularProgress size={20} /> : 'Set Password & Continue'}
                        </Button>
                    </div>
                    <Link component="button" variant="body2" onClick={() => history.push('/logout')} style={{ marginTop: 24 }}>
                        Sign out
                    </Link>
                </div>
            );
        }

        // Email not verified — show verification flow first
        if (emailVerified === false) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
                    <Typography variant="h6" style={{ marginBottom: 16 }}>
                        Your organization requires multi-factor authentication
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
                        You must verify your email address before setting up MFA.
                    </Typography>

                    {verificationError && (
                        <Alert severity="error" style={{ marginBottom: 16, maxWidth: 500 }} onClose={() => setVerificationError('')}>
                            {verificationError}
                        </Alert>
                    )}
                    {verificationMessage && (
                        <Alert severity="success" style={{ marginBottom: 16, maxWidth: 500 }} onClose={() => setVerificationMessage('')}>
                            {verificationMessage}
                        </Alert>
                    )}

                    <div style={{ display: 'flex', gap: 16 }}>
                        <Button
                            variant="outlined"
                            onClick={handleSendVerificationEmail}
                            disabled={sendingVerification}
                        >
                            {sendingVerification ? <CircularProgress size={16} /> : 'Send Verification Email'}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCheckVerification}
                        >
                            I've Verified My Email
                        </Button>
                    </div>
                    <Link component="button" variant="body2" onClick={() => history.push('/logout')} style={{ marginTop: 24 }}>
                        Sign out
                    </Link>
                </div>
            );
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
                <Typography variant="h6" style={{ marginBottom: 16 }}>
                    Your organization requires multi-factor authentication
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
                    Please set up an authenticator app to continue using Nucleus One.
                </Typography>
                <MFAEnrollment onEnrollmentComplete={handleEnrollmentComplete} />
                <Link component="button" variant="body2" onClick={() => history.push('/logout')} style={{ marginTop: 24 }}>
                    Sign out
                </Link>
            </div>
        );
    }

    return children;
};

export default MFAEnrollmentGate;
